import {
  PageHeader, DashboardCard, DashboardGrid, StatusBadge,
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from '@zafroi/ui-kit';
import { usePackagingProfiles } from '@/hooks/useKosh';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import type { StatusVariantMap } from '@zafroi/ui-kit';

const verticalVariants: StatusVariantMap = {
  COMMERCE: { label: 'Commerce', tone: 'info' },
  PROFESSIONAL_SERVICES: { label: 'Professional services', tone: 'info' },
  REAL_ESTATE: { label: 'Real estate', tone: 'info' },
  RECOVERY: { label: 'Recovery', tone: 'info' },
  SOFTWARE: { label: 'Software', tone: 'info' },
};
const cap = (s: string) => s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ');

export function OrganizationPage() {
  const q = usePackagingProfiles();
  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Setup"
        title="Organization"
        description="Business-template profiles — the vertical, terminology and dunning policy Kosh runs by."
      />
      <QueryStateGuard data={q.data} isLoading={q.isLoading} isError={q.isError} emptyTitle="No packaging profile yet">
        {(profiles) => (
          <div className="space-y-6">
            {profiles.map((p) => {
              const labels = Object.entries(p.labels ?? {});
              return (
                <DashboardCard
                  key={p.id}
                  title={p.name}
                  description={p.isDefault ? 'Default profile' : undefined}
                >
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-2 text-[13px]">
                      <StatusBadge status={p.vertical} variants={verticalVariants} />
                      {p.isDefault && <StatusBadge status="DEFAULT" variants={{ DEFAULT: { label: 'Default', tone: 'success' } }} />}
                      <span className="text-muted-foreground">
                        Default billing: {p.defaultBillingSchedule ? cap(p.defaultBillingSchedule) : '—'}
                      </span>
                    </div>
                    <DashboardGrid columns={2}>
                      <div>
                        <h4 className="mb-1.5 text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">Terminology</h4>
                        {labels.length === 0 ? (
                          <p className="text-[13px] text-muted-foreground">Defaults (no overrides).</p>
                        ) : (
                          <div className="space-y-1 text-[13px]">
                            {labels.map(([k, v]) => (
                              <div key={k} className="flex justify-between gap-4">
                                <span className="text-muted-foreground">{k}</span>
                                <span>{String(v)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div>
                        <h4 className="mb-1.5 text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">Dunning ladder</h4>
                        {(p.dunningLadder ?? []).length === 0 ? (
                          <p className="text-[13px] text-muted-foreground">No automated reminders configured.</p>
                        ) : (
                          <Table>
                            <TableHeader><TableRow>
                              <TableHead className="text-right">Step</TableHead><TableHead>Channel</TableHead><TableHead className="text-right">Offset (days)</TableHead>
                            </TableRow></TableHeader>
                            <TableBody>
                              {(p.dunningLadder ?? []).map((d, i) => (
                                <TableRow key={i}>
                                  <TableCell className="text-right tabular-nums">{d.sequenceStep ?? i + 1}</TableCell>
                                  <TableCell>{d.channel ? cap(d.channel) : '—'}</TableCell>
                                  <TableCell className="text-right tabular-nums">{d.offsetDays ?? '—'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </div>
                    </DashboardGrid>
                  </div>
                </DashboardCard>
              );
            })}
          </div>
        )}
      </QueryStateGuard>
    </div>
  );
}
